'use client';

import React, { PropsWithChildren, useCallback } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import PaginationControls from './PaginationControls';
import { BaseCarousel, PaginationType } from 'components/shared/Carousel/BaseCarousel';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { useIsClient } from 'hooks/useIsClient';
import { usePeekCards } from './usePeekCards';

const DEFAULT_ARTICLES_PER_PAGE = 9;
const DEFAULT_GRID_CLASS_NAME =
  'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12 sm:gap-x-8 sm:gap-y-12 lg:gap-x-10 lg:gap-y-14';

export type ArticleGridPaginationType = 'numbered' | 'carousel';

interface ArticleGridPaginatedProps extends PropsWithChildren {
  articlesPerPage?: number;
  gridClassName?: string;
  paginationType?: ArticleGridPaginationType;
}

function buildUrl(pathname: string, params: URLSearchParams, page: number): string {
  const next = new URLSearchParams(params);
  if (page <= 1) {
    next.delete('page');
  } else {
    next.set('page', String(page));
  }
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

const ArticleGridPaginated = ({
  children,
  articlesPerPage = DEFAULT_ARTICLES_PER_PAGE,
  gridClassName = DEFAULT_GRID_CLASS_NAME,
  paginationType = 'numbered',
}: ArticleGridPaginatedProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isClient = useIsClient();
  const { isAboveSm } = useBreakpoint('sm');
  const { isAboveLg } = useBreakpoint('lg');

  const allChildren = React.Children.toArray(children);

  const onPageChange = useCallback(
    (page: number) => {
      const url = buildUrl(pathname, searchParams, page);
      window.history.pushState(null, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [pathname, searchParams],
  );

  const gridRef = usePeekCards();

  if (paginationType === 'carousel') {
    const slidesToScroll = isClient && isAboveLg ? 9 : isClient && isAboveSm ? 6 : 3;
    const totalItems = Math.ceil(allChildren.length / slidesToScroll);

    return (
      <BaseCarousel
        totalItems={totalItems}
        carouselClassName="relative -mx-6 sm:-mx-10 lg:mx-0"
        carouselPaginationClassName="mt-12 px-6 sm:px-10 lg:px-0"
        carouselContentClassName="w-full overflow-x-clip [clip-path:inset(-10px_-100vw_-10px_24px)] sm:[clip-path:inset(-10px_-100vw_-10px_40px)] lg:[clip-path:none]"
        showPaginationThreshold={1}
        paginationType={PaginationType.Numeric}
        carouselOpts={{ align: 'start', slidesToScroll, containScroll: false }}>
        <div ref={gridRef} className={`${gridClassName} ml-6 sm:ml-10 lg:ml-0 cursor-pointer`}>
          {allChildren}
        </div>
      </BaseCarousel>
    );
  }

  const totalPages = Math.max(1, Math.ceil(allChildren.length / articlesPerPage));
  const rawPage = Number(searchParams.get('page')) || 1;
  const currentPage = Math.min(Math.max(1, rawPage), totalPages);

  const startIdx = (currentPage - 1) * articlesPerPage;
  const pageChildren = allChildren.slice(startIdx, startIdx + articlesPerPage);

  return (
    <div>
      <div className={gridClassName}>{pageChildren}</div>
      <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
};

export default ArticleGridPaginated;
